// Demografi. Concept lines:
//   dm_header   Job: orient, which scope and how fresh. Container: plain spacing.
//   dm_scope    Job: show and change the drill-down (Nasional, provinsi,
//               kabupaten/kota). Action: go up a level. Container: one row.
//   dm_filter   Job: narrow everything below (gender, age, generation, zone,
//               status, name or NIK). Container: one card of selects.
//   dm_stats    Job: the scope at a glance. Message: people, men, women, voters.
//               Container: bento, the Ringkasan stat map (lead half from 1024).
//   dm_wilayah  Job: where the volunteers are. Message: ranked regions of the
//               next level with men/women. Action: open a region (drill down).
//               Container: rows with a flat proportional bar.
//   dm_panels   Job: who they are, one chart form per data job (./charts.tsx):
//               donuts for part-to-whole (gender, age group, generation,
//               zone), a population pyramid, columns for ordered axes (month,
//               weekday, decade), polar charts for the 12-step cycles (zodiak,
//               shio), rings for legal-age ratios, a status stack for
//               validation. Container: two stacked columns from 1024.
//   dm_data     Job: see the people behind the charts, full NIK, and open one
//               person's full extraction. Container: table from 1024, records
//               below, paged by 20.
//   dm_cek      Job: decode one NIK on demand. Action: Periksa. Container: form
//               card, then the readout as label/value pairs.
// The ranked region list keeps bars (ranking is a bar job); every mark carries
// its number and share as text, flat fill, no gradient.
import { useId, useState, type CSSProperties, type FormEvent, type ReactNode } from "react";
import { BentoTile, Section } from "@bot-whatsapp/ui";
import { ApiError, useDashboardApi } from "./api";
import { useDelayed, useResource } from "./data";
import { formatClock, formatNumber } from "./format";
import type { Demografi, DemografiScope, NikCheck } from "./types";
import { Columns, Donut, Polar, Pyramid, Rings, StackedBar } from "./charts";
import { EkstraksiDialog, EkstraksiView, STATUS_LABEL } from "./Ekstraksi";
import { LoadError, Notice, RowsSkeleton, ScreenHeader, StatusBadge } from "./ui";

interface Count {
  label: string;
  total: number;
}

const bentoStyle = {
  "--kit-bento-lg": '"b b b a c d"',
  "--kit-bento-lg-cols": "6",
  "--kit-bento-md": '"b b b" "a c d"',
  "--kit-bento-md-cols": "3",
} as CSSProperties;

const WILAYAH_PREVIEW = 12;

export function percent(part: number, whole: number): string {
  if (!whole) return "0%";
  const p = (part / whole) * 100;
  return `${p >= 10 || p === 0 ? Math.round(p) : p.toFixed(1).replace(".", ",")}%`;
}

function Bar({ value, max }: { value: number; max: number }) {
  const width = max ? Math.max(value ? 2 : 0, (value / max) * 100) : 0;
  return (
    <span className="dm-track" aria-hidden="true">
      <span className="dm-fill" style={{ inlineSize: `${width}%` }} />
    </span>
  );
}

function Panel({ title, children, note }: { title: string; children: ReactNode; note?: ReactNode }) {
  const id = useId();
  return (
    <section className="dash-card dm-panel" aria-labelledby={id}>
      <h2 id={id} className="kit-title">
        {title}
      </h2>
      {children}
      {note ? <p className="dm-note">{note}</p> : null}
    </section>
  );
}

function Pairs({ items }: { items: [string, ReactNode][] }) {
  return (
    <dl className="dm-pairs">
      {items.map(([k, v]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

const num = (n: number) => <span className="kit-num">{formatNumber(n)}</span>;

function Wilayah({ data, onOpen }: { data: Demografi; onOpen: (code: string) => void }) {
  const [all, setAll] = useState(false);
  const headingId = useId();
  const { items, level } = data.wilayah;
  const shown = all ? items : items.slice(0, WILAYAH_PREVIEW);
  const max = Math.max(0, ...items.map((i) => i.total));
  const levelName = level === "prov" ? "Provinsi" : level === "kab" ? "Kabupaten/kota" : "Kecamatan";
  const canOpen = !data.scope.kec;
  return (
    <section className="dash-region kit-full" aria-labelledby={headingId}>
      <div className="dash-region-head">
        <h2 id={headingId} className="kit-title">
          Pemetaan {levelName.toLowerCase()}
        </h2>
        <span className="dm-meta">{formatNumber(items.length)} wilayah</span>
      </div>
      {items.length === 0 ? (
        <p className="kit-body">Belum ada NIK di wilayah ini.</p>
      ) : (
        <ol className="dm-regions">
          {shown.map((r) => {
            const body = (
              <>
                <span className="dm-region-name">
                  {r.nama}
                  {!r.dikenal ? <StatusBadge tone="info">Kode lama</StatusBadge> : null}
                </span>
                <Bar value={r.total} max={max} />
                <span className="dm-region-figures">
                  <span className="kit-num dm-region-total">{formatNumber(r.total)}</span>
                  <span className="dm-share">{percent(r.total, data.totals.orang)}</span>
                </span>
                <span className="dm-region-split">
                  L {formatNumber(r.l)} · P {formatNumber(r.p)}
                </span>
              </>
            );
            return (
              <li key={r.code}>
                {canOpen ? (
                  <button type="button" className="dm-region" onClick={() => onOpen(r.code)} aria-label={`Buka ${r.nama}, ${r.total} orang`}>
                    {body}
                  </button>
                ) : (
                  <div className="dm-region">{body}</div>
                )}
              </li>
            );
          })}
        </ol>
      )}
      {items.length > WILAYAH_PREVIEW ? (
        <div>
          <button type="button" className="btn-secondary" onClick={() => setAll((v) => !v)} aria-expanded={all}>
            {all ? "Tampilkan lebih sedikit" : `Tampilkan semua ${formatNumber(items.length)} wilayah`}
          </button>
        </div>
      ) : null}
    </section>
  );
}

function CekNik() {
  const api = useDashboardApi();
  const inputId = useId();
  const headingId = useId();
  const [nik, setNik] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ check: NikCheck; nik: string } | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setResult({ check: await api.checkNik(nik, true), nik: nik.replace(/[\s.-]/g, "") });
    } catch (err) {
      setResult(null);
      setError(err instanceof ApiError && err.status !== 0 ? `Pemeriksaan gagal (${err.message}).` : "Server tidak dapat dihubungi.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="dash-region kit-full" aria-labelledby={headingId}>
      <div className="dash-region-head">
        <h2 id={headingId} className="kit-title">
          Cek NIK
        </h2>
      </div>
      <div className="dm-cek">
        <form className="dash-card dash-form" onSubmit={submit}>
          <div className="field">
            <label htmlFor={inputId}>NIK (16 digit)</label>
            <input
              id={inputId}
              inputMode="numeric"
              autoComplete="off"
              spellCheck={false}
              maxLength={24}
              value={nik}
              onChange={(e) => setNik(e.target.value)}
            />
          </div>
          <div className="dash-actions">
            <button type="submit" className="btn-primary" disabled={busy || nik.trim().length === 0}>
              {busy ? "Memeriksa" : "Periksa"}
            </button>
          </div>
          <p className="dm-note">Aturan keras diperiksa dulu. Bila ada tanda lunak, JEV ikut menilai.</p>
        </form>
        {error ? <Notice tone="danger" title="Tidak dapat memeriksa">{error}</Notice> : null}
        {result ? (
          <div className="dash-card dm-result" aria-live="polite">
            <EkstraksiView check={result.check} nik={/^\d{16}$/.test(result.nik) ? result.nik : undefined} />
          </div>
        ) : null}
      </div>
    </section>
  );
}

type Filters = Omit<DemografiScope, "prov" | "kab" | "kec">;

const AGE_PRESETS: { value: string; label: string; min?: number; max?: number }[] = [
  { value: "", label: "Semua usia" },
  { value: "anak", label: "Anak (di bawah 18)", max: 17 },
  { value: "pemula", label: "Pemilih pemula (17-21)", min: 17, max: 21 },
  { value: "dewasa", label: "Dewasa (18-59)", min: 18, max: 59 },
  { value: "lansia", label: "Lansia (60+)", min: 60 },
  { value: "hakpilih", label: "Punya hak pilih (17+)", min: 17 },
];

function presetOf(f: Filters): string {
  return AGE_PRESETS.find((p) => p.value && p.min === f.umurMin && p.max === f.umurMax)?.value ?? "";
}

/** dm_filter: one card of selects above everything it filters (charts and the data list). */
function FilterBar({ value, onChange }: { value: Filters; onChange: (f: Filters) => void }) {
  const ids = { q: useId(), gender: useId(), age: useId(), gen: useId(), zona: useId(), status: useId() };
  const [q, setQ] = useState(value.q ?? "");
  const set = (patch: Partial<Filters>) => onChange({ ...value, ...patch });
  const active = Object.values(value).filter((v) => v !== undefined && v !== "").length;
  return (
    <form
      className="dash-card dm-filters kit-full"
      aria-label="Filter data"
      onSubmit={(e) => {
        e.preventDefault();
        set({ q: q.trim() || undefined });
      }}
    >
      <div className="dm-filter-grid">
        <div className="field dm-filter-q">
          <label htmlFor={ids.q}>Cari nama atau NIK</label>
          <input
            id={ids.q}
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onBlur={() => set({ q: q.trim() || undefined })}
            enterKeyHint="search"
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <div className="field">
          <label htmlFor={ids.gender}>Jenis kelamin</label>
          <select id={ids.gender} value={value.gender ?? ""} onChange={(e) => set({ gender: (e.target.value || undefined) as Filters["gender"] })}>
            <option value="">Semua</option>
            <option value="L">Laki-laki</option>
            <option value="P">Perempuan</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor={ids.age}>Usia</label>
          <select
            id={ids.age}
            value={presetOf(value)}
            onChange={(e) => {
              const p = AGE_PRESETS.find((x) => x.value === e.target.value)!;
              set({ umurMin: p.min, umurMax: p.max });
            }}
          >
            {AGE_PRESETS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor={ids.gen}>Generasi</label>
          <select id={ids.gen} value={value.generasi ?? ""} onChange={(e) => set({ generasi: (e.target.value || undefined) as Filters["generasi"] })}>
            <option value="">Semua</option>
            {["Gen Alpha", "Gen Z", "Millennial", "Gen X", "Baby Boomer", "Pra-Boomer"].map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor={ids.zona}>Zona waktu</label>
          <select id={ids.zona} value={value.zona ?? ""} onChange={(e) => set({ zona: (e.target.value || undefined) as Filters["zona"] })}>
            <option value="">Semua</option>
            <option value="WIB">WIB</option>
            <option value="WITA">WITA</option>
            <option value="WIT">WIT</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor={ids.status}>Status validasi</label>
          <select id={ids.status} value={value.status ?? ""} onChange={(e) => set({ status: (e.target.value || undefined) as Filters["status"] })}>
            <option value="">Valid dan perlu ditinjau</option>
            <option value="valid">Valid</option>
            <option value="review">Perlu ditinjau</option>
            <option value="rejected">Ditolak JEV</option>
            <option value="invalid">Tidak valid (data lama)</option>
            <option value="semua">Semua status</option>
          </select>
        </div>
      </div>
      <div className="dm-filter-foot">
        <p className="dm-note">{active ? `${active} filter aktif. Berlaku untuk grafik dan daftar data.` : "Tanpa filter. Pilih wilayah lewat pemetaan di bawah."}</p>
        <div className="dash-actions">
          <button type="submit" className="btn-secondary">
            Terapkan
          </button>
          <button
            type="button"
            className="btn-ghost"
            disabled={!active}
            onClick={() => {
              setQ("");
              onChange({});
            }}
          >
            Atur ulang
          </button>
        </div>
      </div>
    </form>
  );
}

const PAGE_SIZE = 20;

/** dm_data: the people behind the charts, one row per NIK, full values; a row opens its extraction. */
function DataList({ query, onOpen }: { query: DemografiScope; onOpen: (submissionId: string) => void }) {
  const api = useDashboardApi();
  const headingId = useId();
  const [page, setPage] = useState(1);
  const key = JSON.stringify(query);
  const [lastKey, setLastKey] = useState(key);
  if (key !== lastKey) {
    setLastKey(key);
    setPage(1);
  }
  const res = useResource(() => api.listData(query, page, PAGE_SIZE), [api, key, page]);
  const data = res.data;
  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  return (
    <section className="dash-region kit-full" aria-labelledby={headingId}>
      <div className="dash-region-head">
        <h2 id={headingId} className="kit-title">
          Data relawan
        </h2>
        <span className="dm-meta">{data ? `${formatNumber(data.total)} orang` : ""}</span>
      </div>
      {res.error && !data ? (
        <LoadError what="Data relawan" onRetry={res.reload} />
      ) : !data ? (
        <RowsSkeleton rows={5} />
      ) : data.items.length === 0 ? (
        <p className="kit-body">Tidak ada data yang cocok dengan filter ini.</p>
      ) : (
        <>
          <div className="dash-table-box">
            <table className="dash-table">
              <thead>
                <tr>
                  <th scope="col">Nama</th>
                  <th scope="col" className="dash-col-nik">NIK</th>
                  <th scope="col">Wilayah</th>
                  <th scope="col">Usia</th>
                  <th scope="col" className="dash-col-status">Status</th>
                  <th scope="col" className="dash-col-reveal">Data</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((r) => (
                  <tr key={r.nik}>
                    <td className="dash-strong">{r.nama}</td>
                    <td className="dash-nowrap kit-num">{r.nik}</td>
                    <td>
                      <span className="dash-cell-lines">
                        <span>{r.kecamatan}</span>
                        <span className="dash-cell-meta">{r.kabkota}</span>
                      </span>
                    </td>
                    <td className="dash-nowrap">
                      <span className="dash-cell-lines">
                        <span>{r.umur === null ? "-" : `${r.umur} tahun`}</span>
                        <span className="dash-cell-meta">{[r.jenisKelamin, r.generasi].filter(Boolean).join(", ")}</span>
                      </span>
                    </td>
                    <td>
                      <StatusBadge tone={STATUS_LABEL[r.status].tone}>{STATUS_LABEL[r.status].text}</StatusBadge>
                    </td>
                    <td className="dash-cell-action">
                      <button type="button" className="btn-secondary" onClick={() => onOpen(r.submissionId)} aria-label={`Ekstraksi ${r.nama}`}>
                        Ekstraksi
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ul className="dash-rows dash-stack">
            {data.items.map((r) => (
              <li key={r.nik} className="dash-row dash-record">
                <p className="dash-row-title">{r.nama}</p>
                <div className="dash-record-action">
                  <StatusBadge tone={STATUS_LABEL[r.status].tone}>{STATUS_LABEL[r.status].text}</StatusBadge>
                </div>
                <dl className="dash-pairs">
                  <div>
                    <dt>NIK</dt>
                    <dd className="kit-num">{r.nik}</dd>
                  </div>
                  <div>
                    <dt>Wilayah</dt>
                    <dd>
                      {r.kecamatan}, {r.kabkota}
                    </dd>
                  </div>
                  <div>
                    <dt>Usia</dt>
                    <dd>{[r.umur === null ? null : `${r.umur} tahun`, r.jenisKelamin, r.generasi].filter(Boolean).join(", ")}</dd>
                  </div>
                </dl>
                <div className="dash-record-delete">
                  <button type="button" className="btn-secondary" onClick={() => onOpen(r.submissionId)} aria-label={`Ekstraksi ${r.nama}`}>
                    Ekstraksi
                  </button>
                </div>
              </li>
            ))}
          </ul>
          <div className="dash-pager">
            <p>
              Halaman {page} dari {pages}
            </p>
            <div className="dash-pager-buttons">
              <button type="button" className="btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Sebelumnya
              </button>
              <button type="button" className="btn-secondary" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
                Berikutnya
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}


export function DemografiScreen() {
  const api = useDashboardApi();
  const headingId = useId();
  const [scope, setScope] = useState<Pick<DemografiScope, "prov" | "kab" | "kec">>({});
  const [filters, setFilters] = useState<Filters>({});
  const [detailId, setDetailId] = useState<string | null>(null);
  const query: DemografiScope = { ...scope, ...filters };
  const queryKey = JSON.stringify(query);
  const res = useResource(() => api.demografi(query), [api, queryKey], { pollMs: 60_000 });
  const slow = useDelayed(res.loading && !res.data);
  const data = res.data;

  const open = (code: string) => {
    if (!scope.prov) setScope({ prov: code });
    else if (!scope.kab) setScope({ prov: scope.prov, kab: code });
    else if (!scope.kec) setScope({ ...scope, kec: code });
  };

  const meta = res.refreshedAt
    ? `Diperbarui pukul ${formatClock(res.refreshedAt)}. Satu orang dihitung sekali per NIK.`
    : res.error
      ? "Data belum dapat dimuat."
      : "Memuat data.";

  const figure = (n: number | undefined, tone?: "l" | "p") =>
    n === undefined ? (
      <span className="dash-skeleton" data-size="figure" data-shown={slow || undefined} aria-hidden="true" />
    ) : tone ? (
      <span className={`dm-fig-${tone}`}>{formatNumber(n)}</span>
    ) : (
      <>{formatNumber(n)}</>
    );

  const total = data?.totals.orang ?? 0;
  const v = data?.validasi;

  return (
    <Section id="demografi" className="dash-screen" composition="custom" variant="demografi" labelledBy={headingId}>
      <ScreenHeader id={headingId} title="Demografi" meta={meta} live />

      <nav className="dm-scope kit-full" aria-label="Cakupan wilayah">
        <button type="button" className="dm-crumb" aria-current={!scope.prov ? "page" : undefined} onClick={() => setScope({})}>
          Nasional
        </button>
        {scope.prov ? (
          <>
            <span className="dm-sep" aria-hidden="true">
              /
            </span>
            <button
              type="button"
              className="dm-crumb"
              aria-current={!scope.kab ? "page" : undefined}
              onClick={() => setScope({ prov: scope.prov })}
            >
              {data?.scope.prov?.nama ?? scope.prov}
            </button>
          </>
        ) : null}
        {scope.kab ? (
          <>
            <span className="dm-sep" aria-hidden="true">
              /
            </span>
            <button
              type="button"
              className="dm-crumb"
              aria-current={!scope.kec ? "page" : undefined}
              onClick={() => setScope({ prov: scope.prov, kab: scope.kab })}
            >
              {data?.scope.kab?.nama ?? scope.kab}
            </button>
          </>
        ) : null}
        {scope.kec ? (
          <>
            <span className="dm-sep" aria-hidden="true">
              /
            </span>
            <span className="dm-crumb" aria-current="page">
              {data?.scope.kec?.nama ?? scope.kec}
            </span>
          </>
        ) : null}
      </nav>

      <FilterBar value={filters} onChange={setFilters} />

      {res.error && !data ? (
        <div className="kit-full">
          <LoadError what="Demografi" onRetry={res.reload} />
        </div>
      ) : (
        <>
          <div className="kit-bento dash-stats" data-has-md="" style={bentoStyle} aria-busy={!data || undefined}>
            <BentoTile
              area="b"
              kind="stat"
              value={figure(data?.totals.orang)}
              body={
                <>
                  Orang terdata
                  <span className="dash-lead-context">
                    {data ? `Usia rata-rata ${data.usia.rataRata ?? "-"} tahun, median ${data.usia.median ?? "-"}.` : "Memuat ringkasan."}
                  </span>
                </>
              }
              signal
            />
            <BentoTile area="a" kind="stat" value={figure(data?.totals.laki, "l")} body={<>Laki-laki{data ? <span className="dash-lead-context">{percent(data.totals.laki, total)}</span> : null}</>} />
            <BentoTile area="c" kind="stat" value={figure(data?.totals.perempuan, "p")} body={<>Perempuan{data ? <span className="dash-lead-context">{percent(data.totals.perempuan, total)}</span> : null}</>} />
            <BentoTile area="d" kind="stat" value={figure(data?.hukum.hakPilih)} body={<>Hak pilih{data ? <span className="dash-lead-context">{percent(data.hukum.hakPilih, total)}</span> : null}</>} />
          </div>

          {!data ? (
            <div className="kit-full">
              <RowsSkeleton rows={6} />
            </div>
          ) : (
            <>
              <Wilayah data={data} onOpen={open} />
              <div className="dm-panels kit-full">
                <div className="dm-col">
                  <Panel title="Jenis kelamin">
                    <Donut
                      caption="Jenis kelamin"
                      centerLabel="orang"
                      data={[
                        { label: "Laki-laki", total: data.totals.laki },
                        { label: "Perempuan", total: data.totals.perempuan },
                      ]}
                    />
                  </Panel>
                  <Panel title="Piramida usia" note={`Rata-rata ${data.usia.rataRata ?? "-"} tahun, median ${data.usia.median ?? "-"} tahun.`}>
                    <Pyramid caption="Piramida usia menurut jenis kelamin" data={data.usia.piramida} />
                  </Panel>
                  <Panel title="Bulan lahir">
                    <Columns caption="Bulan lahir" data={data.bulanLahir} short={(l) => l.slice(0, 3)} />
                  </Panel>
                  <Panel title="Zodiak">
                    <Polar caption="Zodiak" data={data.zodiak} short={(l) => l.slice(0, 3)} />
                  </Panel>
                  <Panel title="Zona waktu asal" note="Dari kode provinsi pada NIK.">
                    <Donut caption="Zona waktu asal" centerLabel="orang" data={data.zonaWaktu} />
                  </Panel>
                  <Panel
                    title="Validasi NIK"
                    note="Aturan keras menolak NIK yang mustahil sebelum disimpan. NIK dengan tanda lunak dinilai JEV; di bawah 60% keyakinan tetap perlu ditinjau."
                  >
                    {v ? (
                      <>
                        <StackedBar
                          caption="Status validasi NIK"
                          colors={["var(--viz-good)", "var(--viz-warning)", "var(--viz-critical)", "var(--color-ink-subtle)"]}
                          data={[
                            { label: "Valid", total: v.valid },
                            { label: "Perlu ditinjau", total: v.review },
                            { label: "Ditolak JEV", total: v.rejected },
                            { label: "Tidak valid (data lama)", total: v.invalid },
                          ]}
                        />
                        <Pairs
                          items={[
                            ["Ditolak bot sebelum disimpan", num(v.ditolakBot)],
                            ["Dinilai JEV", num(v.jevVerified)],
                            ["JEV tidak tersedia", num(v.jevUnverified)],
                          ]}
                        />
                      </>
                    ) : null}
                  </Panel>
                </div>
                <div className="dm-col">
                  <Panel title="Kelompok usia" note="Anak di bawah 18, dewasa 18-59, lansia 60 ke atas.">
                    <Donut caption="Kelompok usia" centerLabel="orang" data={data.usia.kelompok} />
                  </Panel>
                  <Panel title="Generasi" note="Gen Z 1997-2012, Millennial 1981-1996, Gen X 1965-1980.">
                    <Donut caption="Generasi" centerLabel="orang" data={data.generasi.filter((g) => g.total > 0)} />
                  </Panel>
                  <Panel title="Hari lahir">
                    <Columns caption="Hari lahir" data={data.hariLahir} short={(l) => l.slice(0, 3)} />
                  </Panel>
                  <Panel title="Dekade lahir">
                    <Columns caption="Dekade lahir" data={data.dekadeLahir} short={(l) => `${l.slice(2, 4)}an`} />
                  </Panel>
                  <Panel
                    title="Shio"
                    note={data.shio.perkiraan ? `${formatNumber(data.shio.perkiraan)} orang lahir di sekitar Imlek; shionya perkiraan.` : undefined}
                  >
                    <Polar caption="Shio" data={data.shio.items} />
                  </Panel>
                  <Panel
                    title="Kelayakan hukum"
                    note="Batas usia: hak pilih dan KTP-el 17 (UU 7/2017, UU 24/2013), SIM UU 22/2009 Pasal 81, pensiun JP PP 45/2015. Status kawin tidak ada di NIK."
                  >
                    <Rings
                      total={total}
                      items={[
                        { label: "Punya hak pilih", value: data.hukum.hakPilih },
                        { label: "Pemilih pemula (17-21)", value: data.hukum.pemilihPemula },
                        ...data.hukum.sim.map((s) => ({ label: `Cukup umur SIM ${s.label}`, value: s.total })),
                        { label: "Pensiun JP dalam 5 tahun", value: data.hukum.pensiunJp5Tahun },
                      ]}
                    />
                    <Pairs
                      items={[
                        ["Belum punya hak pilih", num(data.hukum.belumHakPilih)],
                        ["Sudah lewat usia pensiun JP", num(data.hukum.lewatUsiaPensiunJp)],
                      ]}
                    />
                  </Panel>
                </div>
              </div>
            </>
          )}
        </>
      )}
      <DataList query={query} onOpen={setDetailId} />
      <CekNik />
      <EkstraksiDialog id={detailId} onClose={() => setDetailId(null)} />
    </Section>
  );
}
